import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Select,
  Row,
  Col,
  Tabs,
  Table,
  Button,
  notification,
  Input,
} from "antd";
import { useSelector } from "react-redux";
import {
  getVehicleModelService,
  getVehicleVarientService,
  getVehicleColorService,
  getVehicleFuelTypeService,
  getVehicleSeaterService,
} from "../../../services/master-services";
import { checkAvailableChassisNumberService } from "../../../services/stock-services";
import { createLeadRequestService } from "../../../services/admin-services";
import { REQUEST_TYPE_IDS } from "../../../utills/constant";
import dayjs from "dayjs";

const AllocateVinModal = ({
  open,
  onCancel,
  onAllocate,
  leadId,
  expectedDeliveryDate,
  customerName,
}) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [varientData, setVarientData] = useState([]);
  const [colorData, setColorData] = useState([]);
  const [fuelData, setFuelData] = useState([]);
  const [seaterData, setSeaterData] = useState([]);
  const [activeTab, setActiveTab] = useState("Physical");
  const [stockData, setStockData] = useState([]);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [selectedChassisNumber, setSelectedChassisNumber] = useState(null);
  const [isRequestLoading, setIsRequestLoading] = useState(false);
  const [confirmationForm] = Form.useForm();
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const leadViewDetails = useSelector((state) => state?.auth?.leadDetails);
  const vehicleDetail = leadViewDetails?.vehicle_detail;
  const isAdmin = useSelector((state) => state?.auth?.isAdmin);

  const [vehicleIds, setVehicleIds] = useState({
    make_id: 1, // Toyota make_id
    model_id: "",
    variant_id: "",
  });

  useEffect(() => {
    if (open && vehicleDetail) {
      // Pre-populate form with vehicle details from leadViewDetails
      const modelId = vehicleDetail.vehicle_model_id;
      const variantId = vehicleDetail.vehicle_variant_id;
      const colorId = vehicleDetail.color_id;
      const fuelTypeId = vehicleDetail.fuel_type_id;
      const seaterId = vehicleDetail.seater_id;

      setVehicleIds({
        make_id: vehicleDetail.make_id || 1,
        model_id: modelId,
        variant_id: variantId,
      });

      // Load all dependent data
      const loadInitialData = async () => {
        setIsLoading(true);
        try {
          // Load models
          await getModalData(vehicleDetail.make_id || 1);

          // Load variants
          if (modelId) {
            await getVarientData(modelId);
          }

          // Load colors, fuel, and seater
          if (modelId && variantId) {
            await Promise.all([
              getColorData(vehicleDetail.make_id || 1, modelId, variantId),
              getFuelData(vehicleDetail.make_id || 1, modelId, variantId),
              getSeaterData(vehicleDetail.make_id || 1, modelId, variantId),
            ]);
          }

          // Set form values
          form.setFieldsValue({
            vehicle_model_id: modelId,
            vehicle_variant_id: variantId,
            color_id: colorId,
            fuel_type_id: fuelTypeId,
            seater_id: seaterId,
          });

          // Fetch stock data after a short delay to ensure form values are set
          setTimeout(() => {
            fetchStockData("Physical");
          }, 100);
        } catch (error) {
          notification.error({
            description:
              error?.response?.data?.message ||
              error?.message ||
              "Failed to load vehicle data",
          });
        } finally {
          setIsLoading(false);
        }
      };

      loadInitialData();
      setActiveTab("Physical");
      setStockData([]);
    } else if (open && !vehicleDetail) {
      // Fallback: if vehicle detail is not available, reset to default
      getModalData(vehicleIds.make_id);
      form.resetFields();
      setVehicleIds({
        make_id: 1,
        model_id: "",
        variant_id: "",
      });
      setVarientData([]);
      setColorData([]);
      setFuelData([]);
      setSeaterData([]);
      setActiveTab("Physical");
      setStockData([]);
    } else {
      // When main modal closes, also close confirmation modal
      setIsConfirmationModalOpen(false);
      setSelectedChassisNumber(null);
      confirmationForm.resetFields();
      setIsRequestLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vehicleDetail]);

  const getModalData = async (make_id) => {
    setIsLoading(true);
    try {
      const modalDataArray = await getVehicleModelService(make_id, true);
      setModalData(modalDataArray?.data?.data || []);
    } catch (error) {
      notification.error({
        description: error.response?.data?.message ?? error?.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getVarientData = async (model_id) => {
    setIsLoading(true);
    try {
      const varientDataArray = await getVehicleVarientService(model_id, true);
      setVarientData(varientDataArray?.data?.data || []);
    } catch (error) {
      notification.error({
        description: error.response?.data?.message ?? error?.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getColorData = async (make_id, model_id, variant_id) => {
    setIsLoading(true);
    try {
      const colorData = await getVehicleColorService(
        make_id,
        model_id,
        variant_id,
        true
      );
      setColorData(colorData?.data?.data || []);
    } catch (error) {
      notification.error({
        description: error.response?.data?.message ?? error?.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getFuelData = async (make_id, model_id, variant_id) => {
    setIsLoading(true);
    try {
      const fuelData = await getVehicleFuelTypeService(
        make_id,
        model_id,
        variant_id,
        null,
        true,
        null
      );
      setFuelData(fuelData?.data?.data || []);
    } catch (error) {
      notification.error({
        description: error.response?.data?.message ?? error?.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getSeaterData = async (make_id, model_id, variant_id) => {
    setIsLoading(true);
    try {
      const seaterData = await getVehicleSeaterService(
        make_id,
        model_id,
        variant_id,
        true
      );
      setSeaterData(seaterData?.data?.data || []);
    } catch (error) {
      notification.error({
        description: error.response?.data?.message ?? error?.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleModelChange = (value) => {
    setVehicleIds((state) => ({
      ...state,
      model_id: value,
    }));

    form.setFieldsValue({
      vehicle_variant_id: undefined,
      color_id: undefined,
      fuel_type_id: undefined,
      seater_id: undefined,
    });
    setVarientData([]);
    setColorData([]);
    setFuelData([]);
    setSeaterData([]);
    getVarientData(value);
  };

  const handleVariantChange = (value) => {
    setVehicleIds((state) => ({
      ...state,
      variant_id: value,
    }));

    form.setFieldsValue({
      color_id: undefined,
      fuel_type_id: undefined,
      seater_id: undefined,
    });
    getColorData(vehicleIds?.make_id, vehicleIds?.model_id, value);
    getFuelData(vehicleIds?.make_id, vehicleIds?.model_id, value);
    getSeaterData(vehicleIds?.make_id, vehicleIds?.model_id, value);
  };

  const handleColorChange = () => {
    // Fetch stock data when color is selected
    fetchStockData();
  };

  const handleFuelChange = () => {
    // Fetch stock data when fuel is selected
    fetchStockData();
  };

  const handleSeaterChange = () => {
    // Fetch stock data when seater is selected
    fetchStockData();
  };

  const fetchStockData = async (tabKey = null) => {
    const values = form.getFieldsValue();
    // Use the provided tabKey or fall back to activeTab state
    const currentTab = tabKey || activeTab;

    // Only call API when all required filters are selected
    if (
      values.vehicle_model_id &&
      values.vehicle_variant_id &&
      values.color_id &&
      values.fuel_type_id &&
      values.seater_id
    ) {
      setIsLoading(true);
      try {
        // Build base params with required fields
        const params = {
          vehicle_model_id: values.vehicle_model_id,
          vehicle_variant_id: values.vehicle_variant_id,
          color_id: values.color_id,
          fuel_type_id: values.fuel_type_id,
          seater_id: values.seater_id,
        };

        // Add tab-specific parameters (1 = yes/selected, 0 = no/not selected)
        if (currentTab === "Physical") {
          params.stock_status = "Physical";
          params.delivery_denied = 0;
          params.allocated = 0;
          params.requested = 0;
        } else if (currentTab === "Transit") {
          params.stock_status = "Transit";
          params.delivery_denied = 0;
          params.allocated = 0;
          params.requested = 0;
        } else if (currentTab === "MDDP") {
          params.stock_status = "MDDP";
          params.delivery_denied = 0;
          params.allocated = 0;
          params.requested = 0;
        } else if (currentTab === "Denied") {
          params.delivery_denied = 1;
          params.allocated = 0;
          params.requested = 0;
        } else if (currentTab === "Requested") {
          params.requested = 1;
          params.delivery_denied = 0;
          params.allocated = 0;
        } else if (currentTab === "Allocated") {
          params.allocated = 1;
          params.delivery_denied = 0;
          params.requested = 0;
        }

        const res = await checkAvailableChassisNumberService(params);
        const stockList = res?.data?.data || [];

        // Map API response to table data structure
        const mappedData = stockList.map((item, index) => ({
          key: item.id || index,
          id: item.id,
          lead_id: item.lead_id,
          customer_name: item.customer_name,
          chassis_number: item.chassis_number,
          stock_age: item.stock_age,
          physical_location_id: item.physical_location_id,
          physical_location_name: item.physical_location_name,
          expected_delivery_date: item.expected_delivery_date,
          requestor_name: item.requestor_name,
          requestor_role_name: item.requestor_role_name,
          requested_date: item.requested_date,
          request_age: item.request_age,
          allocation_age: item.allocation_age,
          allocated_by: item.allocated_by,
          allocated_role_name: item.allocated_role_name,
          allocated_date: item.allocated_date,
          allocation_status: item.allocation_status,
          delivery_denied_remarks: item.delivery_denied_remarks,
          mddp_month: item.mddp_month,
          status: currentTab,
        }));

        setStockData(mappedData);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch stock data";
        notification.error({
          description: errorMessage,
        });
        setStockData([]);
      } finally {
        setIsLoading(false);
      }
    } else {
      // Clear stock data if filters are not complete
      setStockData([]);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    // Fetch stock data when tab changes, passing the new tab key directly
    fetchStockData(key);
  };

  const handleAllocate = (record) => {
    setSelectedChassisNumber(record.chassis_number);
    setIsConfirmationModalOpen(true);
  };

  const handleView = (record) => {
    setSelectedRecord(record);
    setIsViewModalOpen(true);
  };

  const handleViewModalClose = () => {
    setIsViewModalOpen(false);
    setSelectedRecord(null);
  };

  const handleConfirmationCancel = () => {
    // Don't allow closing while request is in progress
    if (isRequestLoading) {
      return;
    }
    setIsConfirmationModalOpen(false);
    setSelectedChassisNumber(null);
    confirmationForm.resetFields();
  };

  const handleConfirmAllocate = async () => {
    // Validate confirmation form
    let values;
    try {
      values = await confirmationForm.validateFields();
    } catch (err) {
      return; // Validation failed
    }

    // Required data check
    if (!leadId || !selectedChassisNumber) {
      notification.error({ description: "Missing required information" });
      return;
    }

    setIsRequestLoading(true);

    try {
      const payload = {
        lead_id: leadId,
        chassis_number: selectedChassisNumber,
        request_creation_remarks: values.remarks || "",
        request_type_id: REQUEST_TYPE_IDS.ALLOCATION_REQUEST,
      };

      const res = await createLeadRequestService(payload);

      if (res?.data?.status === 200 || res?.data?.status === 201) {
        const message = res?.data?.message || "Request created successfully";
        const chassisNum = selectedChassisNumber;

        setIsRequestLoading(false);

        // STEP 1: Close confirmation modal
        setIsConfirmationModalOpen(false);
        confirmationForm.resetFields();
        setSelectedChassisNumber(null);

        // STEP 2: Close main modal after confirmation modal fully closes
        setTimeout(() => {
          onCancel?.(); // parent modal close
        }, 150);

        // STEP 3: Show success message + callback AFTER modals are closed
        setTimeout(() => {
          notification.success({ message });
          onAllocate?.({ chassis_number: chassisNum });
        }, 300);
      } else {
        setIsRequestLoading(false);
        notification.error({
          message: res?.data?.message || "Failed to create request",
        });
      }
    } catch (error) {
      setIsRequestLoading(false);

      notification.error({
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to create request",
      });
    }
  };

  const getColumns = () => {
    const baseColumns = [
      {
        title: "Chassis Number",
        dataIndex: "chassis_number",
        key: "chassis_number",
      },
    ];

    // Denied tab: Chassis Number, Stock Age, Location, Reason, Allocate
    if (activeTab === "Denied") {
      baseColumns.push({
        title: "Stock Age",
        dataIndex: "stock_age",
        key: "stock_age",
      });
      baseColumns.push({
        title: "Location",
        dataIndex: "physical_location_name",
        key: "physical_location_name",
      });
      baseColumns.push({
        title: "Reason",
        dataIndex: "delivery_denied_remarks",
        key: "delivery_denied_remarks",
        render: (text) => (
          <span
            style={{ color: "#1890ff", cursor: "pointer" }}
            title={text || "-"}
          >
            {text || "-"}
          </span>
        ),
      });
    }
    // Physical tab: Chassis Number, Stock Age, Location, Allocate
    else if (activeTab === "Physical") {
      baseColumns.push({
        title: "Stock Age",
        dataIndex: "stock_age",
        key: "stock_age",
      });
      baseColumns.push({
        title: "Location",
        dataIndex: "physical_location_name",
        key: "physical_location_name",
      });
    }
    // Transit tab: Chassis Number, Location, Allocate
    else if (activeTab === "Transit") {
      baseColumns.push({
        title: "Location",
        dataIndex: "physical_location_name",
        key: "physical_location_name",
      });
    }
    // MDDP tab: Chassis Number, MDDP Month, Allocate
    else if (activeTab === "MDDP") {
      baseColumns.push({
        title: "MDDP Month",
        dataIndex: "mddp_month",
        key: "mddp_month",
        render: (text) => {
          if (!text) return "-";
          // MDDP month comes already formatted as "Nov 2025" from API
          return text;
        },
      });
    }
    // Requested tab: Chassis Number, Stock Age, Location, Requested By, Age of Request, Expected Date of Retail, View
    else if (activeTab === "Requested") {
      baseColumns.push({
        title: "Stock Age",
        dataIndex: "stock_age",
        key: "stock_age",
      });
      baseColumns.push({
        title: "Location",
        dataIndex: "physical_location_name",
        key: "physical_location_name",
      });
      baseColumns.push({
        title: "Requested By",
        dataIndex: "requestor_name",
        key: "requestor_name",
        render: (text, record) => {
          if (text && record.requestor_role_name) {
            return `${text} (${record.requestor_role_name})`;
          }
          return text || "-";
        },
      });
      baseColumns.push({
        title: "Age of Request",
        dataIndex: "request_age",
        key: "request_age",
      });
      baseColumns.push({
        title: "Expected Date of Retail",
        dataIndex: "expected_delivery_date",
        key: "expected_delivery_date",
        render: (text) => (text ? dayjs(text).format("DD/MM/YYYY") : "-"),
      });
    }
    // Allocated tab: Chassis Number, Stock Age, Location, Allocated By, Age of Allocation, Expected Date of Retail, View
    else if (activeTab === "Allocated") {
      baseColumns.push({
        title: "Stock Age",
        dataIndex: "stock_age",
        key: "stock_age",
      });
      baseColumns.push({
        title: "Location",
        dataIndex: "physical_location_name",
        key: "physical_location_name",
      });
      baseColumns.push({
        title: "Allocated By",
        dataIndex: "allocated_by",
        key: "allocated_by",
        render: (text, record) => {
          if (text && record.allocated_role_name) {
            return `${text} (${record.allocated_role_name})`;
          }
          return text || "-";
        },
      });
      baseColumns.push({
        title: "Age of Allocation",
        dataIndex: "allocation_age",
        key: "allocation_age",
      });
      baseColumns.push({
        title: "Expected Date of Retail",
        dataIndex: "expected_delivery_date",
        key: "expected_delivery_date",
        render: (text) => (text ? dayjs(text).format("DD/MM/YYYY") : "-"),
      });
    }

    // Show View button for Requested and Allocated tabs, Allocate button for others
    if (activeTab === "Requested" || activeTab === "Allocated") {
      baseColumns.push({
        title: "Action",
        key: "action",
        render: (_, record) => (
          <Button type="link" onClick={() => handleView(record)}>
            View
          </Button>
        ),
      });
    } else {
      baseColumns.push({
        title: "Action",
        key: "action",
        render: (_, record) => (
          <Button
            type="primary"
            className="secondary-button"
            onClick={() => handleAllocate(record)}
          >
            Allocate
          </Button>
        ),
      });
    }

    return baseColumns;
  };

  const getFilteredStockData = () => {
    // Since we're already filtering by stock_status in the API call, just return stockData
    return stockData;
  };

  return (
    <Modal
      title={
        <div>
          <h5
            className="text-center mb-0"
            style={{ fontSize: "20px", fontWeight: "500" }}
          >
            Select a Car
          </h5>
          <hr style={{ margin: "15px 0 0 0" }} />
        </div>
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      maskClosable={false}
      width={1000}
    >
      <Form form={form} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
          <Col xl={8} lg={8} md={12} sm={12} xs={24}>
            <Form.Item
              name="vehicle_model_id"
              label="Model"
              rules={[
                {
                  required: true,
                  message: "Please select model",
                },
              ]}
            >
              <Select
                placeholder="Model"
                onChange={handleModelChange}
                className="form-control-ant"
                disabled={!!vehicleDetail}
              >
                {modalData?.map((option) => (
                  <Select.Option key={option.id} value={option.id}>
                    {option?.model_name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xl={8} lg={8} md={12} sm={12} xs={24}>
            <Form.Item
              name="vehicle_variant_id"
              label="Variant"
              rules={[
                {
                  required: true,
                  message: "Please select variant",
                },
              ]}
            >
              <Select
                onChange={handleVariantChange}
                className="form-control-ant"
                placeholder="Variant"
                disabled={!!vehicleDetail || !vehicleIds.model_id}
              >
                {varientData?.map((option) => (
                  <Select.Option key={option.id} value={option.id}>
                    {option?.variant_code}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xl={8} lg={8} md={12} sm={12} xs={24}>
            <Form.Item
              name="color_id"
              label="Color"
              rules={[
                {
                  required: true,
                  message: "Please select color",
                },
              ]}
            >
              <Select
                className="form-control-ant"
                placeholder="Color"
                onChange={handleColorChange}
                disabled={!!vehicleDetail || !vehicleIds.variant_id}
              >
                {colorData?.map((option) => (
                  <Select.Option key={option.id} value={option.id}>
                    {option?.color}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xl={8} lg={8} md={12} sm={12} xs={24}>
            <Form.Item
              name="fuel_type_id"
              label="Fuel"
              rules={[
                {
                  required: true,
                  message: "Please select fuel",
                },
              ]}
            >
              <Select
                className="form-control-ant"
                placeholder="Fuel"
                onChange={handleFuelChange}
                disabled={!!vehicleDetail || !vehicleIds.variant_id}
              >
                {fuelData?.map((option) => (
                  <Select.Option key={option.id} value={option.id}>
                    {option?.title}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xl={8} lg={8} md={12} sm={12} xs={24}>
            <Form.Item
              name="seater_id"
              label="Seater"
              rules={[
                {
                  required: true,
                  message: "Please select seater",
                },
              ]}
            >
              <Select
                className="form-control-ant"
                placeholder="Seater"
                onChange={handleSeaterChange}
                disabled={!!vehicleDetail || !vehicleIds.variant_id}
              >
                {seaterData?.map((option) => (
                  <Select.Option key={option.id} value={option.id}>
                    {option?.seater}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={[
            {
              key: "Denied",
              label: "Denied",
              children: (
                <Table
                  columns={getColumns()}
                  dataSource={getFilteredStockData()}
                  loading={isLoading}
                  pagination={false}
                  locale={{
                    emptyText: "No stock available",
                  }}
                  scroll={{ x: "max-content" }}
                />
              ),
            },
            {
              key: "Physical",
              label: "Physical",
              children: (
                <Table
                  columns={getColumns()}
                  dataSource={getFilteredStockData()}
                  loading={isLoading}
                  pagination={false}
                  locale={{
                    emptyText: "No stock available",
                  }}
                  scroll={{ x: "max-content" }}
                />
              ),
            },
            {
              key: "Transit",
              label: "Transit",
              children: (
                <Table
                  columns={getColumns()}
                  dataSource={getFilteredStockData()}
                  loading={isLoading}
                  pagination={false}
                  locale={{
                    emptyText: "No stock available",
                  }}
                  scroll={{ x: "max-content" }}
                />
              ),
            },
            {
              key: "MDDP",
              label: "MDDP",
              children: (
                <Table
                  columns={getColumns()}
                  dataSource={getFilteredStockData()}
                  loading={isLoading}
                  pagination={false}
                  locale={{
                    emptyText: "No stock available",
                  }}
                  scroll={{ x: "max-content" }}
                />
              ),
            },
            {
              key: "Requested",
              label: "Requested",
              children: (
                <Table
                  columns={getColumns()}
                  dataSource={getFilteredStockData()}
                  loading={isLoading}
                  pagination={false}
                  locale={{
                    emptyText: "No stock available",
                  }}
                  scroll={{ x: "max-content" }}
                />
              ),
            },
            {
              key: "Allocated",
              label: "Allocated",
              children: (
                <Table
                  columns={getColumns()}
                  dataSource={getFilteredStockData()}
                  loading={isLoading}
                  pagination={false}
                  locale={{
                    emptyText: "No stock available",
                  }}
                  scroll={{ x: "max-content" }}
                />
              ),
            },
          ]}
        />
      </Form>

      {/* Confirmation Modal */}
      <Modal
        title="Request Allocation"
        open={isConfirmationModalOpen}
        onCancel={handleConfirmationCancel}
        footer={[
          <Button
            key="cancel"
            onClick={handleConfirmationCancel}
            disabled={isRequestLoading}
          >
            Cancel
          </Button>,
          <Button
            key="allocate"
            type="primary"
            onClick={handleConfirmAllocate}
            loading={isRequestLoading}
          >
            {isAdmin ? "Allocate" : "Request"}
          </Button>
        ]}
        maskClosable={false}
        closable={false}
      >
        <Form form={confirmationForm} layout="vertical">
          <div style={{ marginBottom: "16px" }}>
            <p>
              You are about to allocate Chassis Number{" "}
              <strong>{selectedChassisNumber}</strong> to the Customer{" "}
              <strong>{customerName || "N/A"}</strong>. Expected date of
              delivery is{" "}
              <strong>
                {expectedDeliveryDate
                  ? dayjs(expectedDeliveryDate).format("DD/MM/YYYY")
                  : "N/A"}
              </strong>
              . Please click <strong>Allocate</strong> to proceed or{" "}
              <strong>Cancel</strong>.
            </p>
          </div>
          <Form.Item
            name="remarks"
            label="Remarks"
            rules={[{ required: false }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Type the remarks if required"
              className="form-control-ant"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* View Car Allocation Info Modal */}
      <Modal
        title="Car Allocation Info"
        open={isViewModalOpen}
        onCancel={handleViewModalClose}
        footer={[
          <Button key="close" type="primary" onClick={handleViewModalClose}>
            Close
          </Button>,
        ]}
        width={600}
      >
        {selectedRecord && (
          <div>
            {activeTab === "Requested" ? (
              <>
                <div style={{ marginBottom: "16px" }}>
                  <p style={{ marginBottom: "8px" }}>
                    <strong>Requested For:</strong>{" "}
                    {selectedRecord.customer_name ? (
                      <>
                        {selectedRecord.customer_name}
                        {selectedRecord.lead_id &&
                          `, Lead No. ${selectedRecord.lead_id}`}
                      </>
                    ) : (
                      "-"
                    )}
                  </p>
                  <p style={{ marginBottom: "8px" }}>
                    <strong>Chassis Number:</strong>{" "}
                    {selectedRecord.chassis_number}
                  </p>
                  <p style={{ marginBottom: "8px" }}>
                    <strong>Date of Request:</strong>{" "}
                    {selectedRecord.requested_date
                      ? dayjs(selectedRecord.requested_date).format(
                          "DD MMM YYYY"
                        )
                      : "-"}
                  </p>
                  <p style={{ marginBottom: "8px" }}>
                    <strong>Requested By:</strong>{" "}
                    {selectedRecord.requestor_name ? (
                      <>
                        {selectedRecord.requestor_name}
                        {selectedRecord.requestor_role_name &&
                          ` (${selectedRecord.requestor_role_name})`}
                      </>
                    ) : (
                      "-"
                    )}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div style={{ marginBottom: "16px" }}>
                  <p style={{ marginBottom: "8px" }}>
                    <strong>Allocated To:</strong>{" "}
                    {selectedRecord.customer_name ? (
                      <>
                        {selectedRecord.customer_name}
                        {selectedRecord.lead_id &&
                          `, Lead No. ${selectedRecord.lead_id}`}
                      </>
                    ) : (
                      "-"
                    )}
                  </p>
                  <p style={{ marginBottom: "8px" }}>
                    <strong>Chassis Number:</strong>{" "}
                    {selectedRecord.chassis_number}
                  </p>
                  <p style={{ marginBottom: "8px" }}>
                    <strong>Date of Allocation:</strong>{" "}
                    {selectedRecord.allocated_date
                      ? dayjs(selectedRecord.allocated_date).format(
                          "DD MMM YYYY"
                        )
                      : "-"}
                  </p>
                  <p style={{ marginBottom: "8px" }}>
                    <strong>Allocated By:</strong>{" "}
                    {selectedRecord.allocated_by ? (
                      <>
                        {selectedRecord.allocated_by}
                        {selectedRecord.allocated_role_name &&
                          ` (${selectedRecord.allocated_role_name})`}
                      </>
                    ) : (
                      "-"
                    )}
                  </p>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
    </Modal>
  );
};

export default AllocateVinModal;

